// NPM
import { omitBy, isNull, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";

// UTILITY
import errorHandler from "Utils/error-handler";

// HOOKS
import useQuery from "Hooks/query.hook";
import { ToTitleCase } from "Helpers/to-title-case";

//data
import vehicleTypes from "Data/vehicle-types.json";

// STYLE
import "../delivery-forms.scss";

// COMPONENTS
import { Col, Row } from "antd";
import { AlertBox, Button, SelectInput, TextInput } from "@life-line-lab/components-react";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { VehicleTypeFormSchema } from "./vehicle-type-form-validation";
import useDeliveryForms from "../use-delivery-forms";
import { VehicleTypeEnums } from "Data/settings";

interface VehicleTypeFormProps {
  onSubmit: (data: any) => void;
  onUpdate: (data: any) => void;
  onFetch: (vehicleID: string) => void;
  data?: any;
}

const engineOptions = Object.values(VehicleTypeEnums);

const VehicleTypeForm: React.FC<VehicleTypeFormProps> = ({ onSubmit, onUpdate, onFetch, data }) => {
  const query = useQuery();
  const id = query.get("id");
  const { t } = useTranslation();

  const { input, setInput, error, setError, handleFieldChange, handleSelectChange } =
    useDeliveryForms();

  useEffect(() => {
    id && onFetch(id);
  }, []);

  useEffect(() => {
    if (!isEmpty(data)) {
      setInput({
        ...input,
        _id: data._id,
        name: data.name,
        engineType: data.engineType,
        maxWeight: data.maxWeight,
        capacity: data.capacity,
        maxDimension: data.maxDimension,
        emissionFactor: data.emissionFactor,
      });
    }
  }, [data]);

  const handleSubmit = () => {
    try {
      const valInput = omitBy(input, isEmpty);
      const value = VehicleTypeFormSchema.validateSync(valInput, {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      if (value) id ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      console.log("Error:", path, value);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="delivery-form vehicle-type-form">
      <form className="svf-container">
        <div className="main-header">
          <span className="center header-title">
            {t(id ? "label.editField" : "label.addField", {
              field: t("label.vehicle"),
            }).toUpperCase()}
          </span>
        </div>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <TextInput
              isRequired={true}
              label={t("label.vehicleType")}
              value={input?.name}
              name="name"
              onChange={handleFieldChange}
            />
            <AlertBox text={error?.name} />
          </Col>
          <Col span={24}>
            <SelectInput
              showSearch
              isRequired={true}
              label={t("label.engineType")}
              value={input?.engineType}
              name="engineType"
              options={engineOptions.map((e) => ({
                label: t(`option.vtype_${e}` as any),
                value: e,
              }))}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              onChange={(value) => handleSelectChange(value, "engineType")}
            />
            <AlertBox text={error?.engineType} />
          </Col>
          <Col span={24}>
            <TextInput
              isRequired={true}
              label={t("label.maximumWeight")}
              value={input?.maxWeight}
              name="maxWeight"
              onChange={handleFieldChange}
            />
            <AlertBox text={error?.maxWeight} />
          </Col>
          <Col span={24}>
            <TextInput
              isRequired={true}
              label={t("label.capacity")}
              value={input?.capacity}
              name="capacity"
              onChange={handleFieldChange}
            />
            <AlertBox text={error?.capacity} />
          </Col>
          <Col span={24}>
            <TextInput
              isRequired={true}
              label={t("label.maxDimension")}
              value={input?.maxDimension}
              name="maxDimension"
              onChange={handleFieldChange}
            />
            <AlertBox text={error?.maxDimension} />
          </Col>
          <Col span={24}>
            <TextInput
              isRequired={true}
              label={t("label.emissionFactor")}
              value={input?.emissionFactor}
              name="emissionFactor"
              onChange={handleFieldChange}
            />
            <AlertBox text={error?.emissionFactor} />
          </Col>
        </Row>
        <div className="svf-btn-container">
          <Button
            size="large"
            type="primary"
            onClick={handleSubmit}
            label={id ? t("action.update") : t("action.create")}
          />
        </div>
      </form>
    </div>
  );
};

export default VehicleTypeForm;
